<script setup lang="ts">
import '../assets/style/fonts.css'

import { AnnotationSetType, SentenceType } from 'types/types'
import { computed, onMounted, ref, StyleValue, watch } from 'vue'

import { useColumnJustification } from '@/composables/useColumnJustification'
import {
  BOUNDARY_ELEMENT_ID_SUFFIX,
  COLUMN_LINE_FILLER_CLASS,
  NUMBER_ELEMENT_ID_SUFFIX,
  SENTENCE_ELEMENT_ID_SUFFIX,
  STRETCH_POINT_CLASS,
  textSegmentId,
  WORD_ELEMENT_ID_SUFFIX,
} from '@/constants/practiceConstants'
import { AnnotatedCharacter, characterStyleToCss } from '@/util/AnnotatedCharacter'
import { AnnotatedSegment } from '@/util/AnnotatedSegment'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { AnnotatedWord } from '@/util/AnnotatedWord'
import { isCharacter } from '@/util/CharacterClasses'
import { DEFAULT_FONT_SIZE, DEFAULT_LINE_HEIGHT } from '@/util/FontUtil'
import { SegmentationType } from '@/util/Segmentation'
import { regularStyle } from '@/util/TextStyles'
import { defaultTransformation, TransformationType } from '@/util/Transformations'

interface Props {
  // parent asks us to render these sentences
  annotations?: Array<AnnotationSetType>
  sentences: Array<SentenceType>
  segmentations?: Array<SegmentationType>
  transformation?: TransformationType
  name: string
  defaultFont?: string
  fontSize?: number
  lineHeight?: number

  // parent utilities
  startIndex?: number | null
  breakBeforeLine?: () => boolean
  showResponsiveHeaders?: () => boolean
  showSentenceNumbers?: boolean
  suppressColors?: (sentenceIndex?: number, segmentIndex?: number) => boolean
  columnMode?: boolean
  showEndOfSentenceCharacter?: boolean
  showNewlineIndicators?: boolean
  clickableLineNumber?: ((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => boolean) | null
  clickableSentence?: ((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => boolean) | null
  clickableSegment?:
    | ((annotatedSentence: AnnotatedSentence, annotatedSegment: AnnotatedSegment, sentenceIndex: number, segmentIndex: number) => boolean)
    | null
  clickableSegmentBoundary?:
    | ((annotatedSentence: AnnotatedSentence, annotatedSegment: AnnotatedSegment, sentenceIndex: number, segmentIndex: number) => boolean)
    | null
  clickableResponsiveHeader?: ((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => boolean) | null
  clickableWord?:
    | ((
        annotatedSentence: AnnotatedSentence,
        annotatedSegment: AnnotatedSegment,
        annotatedWord: AnnotatedWord,
        sentenceIndex: number,
        segmentIndex: number,
        wordIndex: number,
      ) => boolean)
    | null
  clickableWordBoundary?:
    | ((
        annotatedSentence: AnnotatedSentence,
        annotatedSegment: AnnotatedSegment,
        annotatedWord: AnnotatedWord,
        sentenceIndex: number,
        segmentIndex: number,
        wordIndex: number,
      ) => boolean)
    | null
  clickableChar?:
    | ((
        annotatedSentence: AnnotatedSentence,
        annotatedSegment: AnnotatedSegment,
        annotatedWord: AnnotatedWord,
        annotatedChar: AnnotatedCharacter,
        sentenceIndex: number,
        segmentIndex: number,
        wordIndex: number,
        charIndex: number,
      ) => boolean)
    | null
  draggableChar?:
    | ((
        annotatedSentence: AnnotatedSentence,
        annotatedSegment: AnnotatedSegment,
        annotatedWord: AnnotatedWord,
        annotatedChar: AnnotatedCharacter,
        sentenceIndex: number,
        segmentIndex: number,
        wordIndex: number,
        charIndex: number,
      ) => boolean)
    | null
  isCharSelected?: ((char: AnnotatedCharacter) => string) | null
  isSegmentBoundarySelected?: ((segment: AnnotatedSegment) => string) | null
  isWordBoundarySelected?: ((word: AnnotatedWord) => string) | null
  sentenceInlineStyle?: ((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => Record<string, string> | undefined) | null
  sentenceStyle?: ((sentenceStyle: string, annotatedSentence: AnnotatedSentence, sentenceIndex: number) => string) | null
  segmentStyle?:
    | ((
        style: string,
        annotatedSentence: AnnotatedSentence,
        annotatedSegment: AnnotatedSegment,
        sentenceIndex: number,
        segmentIndex: number,
      ) => string)
    | null
  segmentBoundaryStyle?:
    | ((
        style: string,
        annotatedSentence: AnnotatedSentence,
        annotatedSegment: AnnotatedSegment,
        sentenceIndex: number,
        segmentIndex: number,
      ) => string)
    | null
  wordStyle?:
    | ((
        wordStyle: string,
        annotatedSentence: AnnotatedSentence,
        annotatedSegment: AnnotatedSegment,
        annotatedWord: AnnotatedWord,
        sentenceIndex: number,
        segmentIndex: number,
        wordIndex: number,
      ) => string)
    | null
  wordBoundaryStyle?:
    | ((
        wordStyle: string,
        annotatedSentence: AnnotatedSentence,
        annotatedSegment: AnnotatedSegment,
        annotatedWord: AnnotatedWord,
        sentenceIndex: number,
        segmentIndex: number,
        wordIndex: number,
      ) => string)
    | null
  charStyle?:
    | ((
        styleClass: string,
        annotatedSentence: AnnotatedSentence,
        annotatedSegment: AnnotatedSegment,
        annotatedWord: AnnotatedWord,
        annotatedChar: AnnotatedCharacter,
        sentenceIndex: number,
        segmentIndex: number,
        wordIndex: number,
        charIndex: number,
      ) => string)
    | null

  // for parent to track
  annotatedSentences?: Array<AnnotatedSentence>
  annotatedSegments?: Array<Array<AnnotatedSegment>>
  annotatedWords?: Array<Array<AnnotatedWord>>
  vShow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  annotatedSegments: () => [],
  annotatedSentences: () => [],
  annotatedWords: () => [],
  annotations: () => [],
  breakBeforeLine: () => false,
  charStyle: null,
  clickableChar: null,
  clickableLineNumber: null,
  clickableResponsiveHeader: null,
  clickableSegment: null,
  clickableSegmentBoundary: null,
  clickableSentence: null,
  clickableWord: null,
  clickableWordBoundary: null,
  columnMode: false,
  defaultFont: undefined,
  draggableChar: null,
  fontSize: DEFAULT_FONT_SIZE,
  isCharSelected: null,
  isSegmentBoundarySelected: null,
  isWordBoundarySelected: null,
  lineHeight: DEFAULT_LINE_HEIGHT,
  segmentBoundaryStyle: null,
  segmentStyle: null,
  segmentations: () => [],
  sentenceInlineStyle: null,
  sentenceStyle: null,
  showEndOfSentenceCharacter: false,
  showNewlineIndicators: false,
  showResponsiveHeaders: () => false,
  showSentenceNumbers: false,
  startIndex: 0,
  suppressColors: () => false,
  transformation: () => defaultTransformation(),
  vShow: true,
  wordBoundaryStyle: null,
  wordStyle: null,
})

interface CharacterCluster {
  annotatedChar: AnnotatedCharacter
  charIndex: number
  displayText: string
}

function clusterCharacters(chars: Array<AnnotatedCharacter | null>): Array<CharacterCluster> {
  const clusters: CharacterCluster[] = []
  let i = 0
  while (i < chars.length) {
    const char = chars[i]
    if (!char) {
      i++
      continue
    }

    if (isCharacter(char.character())) {
      const charIndex = i
      let text = char.character()
      i++
      while (i < chars.length && chars[i] && !isCharacter(chars[i]!.character())) {
        text += chars[i]!.character()
        i++
      }
      clusters.push({ annotatedChar: char, charIndex, displayText: text })
    } else {
      // Standalone combining mark (edge case)
      clusters.push({ annotatedChar: char, charIndex: i, displayText: char.character() })
      i++
    }
  }
  return clusters
}

const emit = defineEmits([
  'clickChar',
  'clickLineNumber',
  'clickResponsiveHeader',
  'clickSegment',
  'clickSegmentBoundary',
  'clickSentence',
  'clickWord',
  'clickWordBoundary',
  'dblclickChar',
  'mousedownChar',
  'mouseenterChar',
  'mouseupText',
  'update:annotatedSegments',
  'update:annotatedSentences',
  'update:annotatedWords',
])

const displayStyle = computed<StyleValue>(() => {
  return { display: props.vShow ? '' : 'none' } as StyleValue
})

const myAnnotatedSegments = computed<Array<Array<AnnotatedSegment>>>(() => {
  const segments = [] as Array<Array<AnnotatedSegment>>
  if (!myAnnotatedSentences.value.length) {
    return segments
  }

  myAnnotatedSentences.value.forEach((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => {
    segments.push(new Array<AnnotatedSegment>())
    annotatedSentence.forEach((annotatedSegment: AnnotatedSegment) => {
      segments[sentenceIndex].push(annotatedSegment)
    })
  })

  return segments
})

const annotatedChars = computed<Array<Array<Array<Array<AnnotatedCharacter | null>>>>>(() => {
  const characters = [] as Array<Array<Array<Array<AnnotatedCharacter | null>>>>
  if (!myAnnotatedSentences.value.length) {
    return characters
  }

  myAnnotatedSentences.value.forEach((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => {
    characters.push(new Array<Array<Array<AnnotatedCharacter>>>())
    annotatedSentence.forEach((annotatedSegment: AnnotatedSegment, __: string | null, segmentIndex = 0) => {
      characters[sentenceIndex].push(new Array<Array<AnnotatedCharacter>>())
      annotatedSegment.forEach((annotatedWord: AnnotatedWord, __: string | null, wordIndex = 0) => {
        characters[sentenceIndex][segmentIndex].push(new Array<AnnotatedCharacter>())
        annotatedWord.forEach((annotatedCharacter: AnnotatedCharacter | null) => {
          characters[sentenceIndex][segmentIndex][wordIndex].push(annotatedCharacter)
        })
      })
    })
  })

  return characters
})

const clusteredChars = computed<Array<Array<Array<Array<CharacterCluster>>>>>(() => {
  if (!annotatedChars.value.length) {
    return []
  }
  return annotatedChars.value.map((sentenceChars) =>
    sentenceChars.map((segmentChars) => segmentChars.map((wordChars) => clusterCharacters(wordChars))),
  )
})

// Per-char spans are needed for: char-level styling, click/drag handlers, or when annotations
// with overrideStyle exist (so character colors render even outside coloration mode).
const hasCharAnnotations = computed(() => props.annotations.some((a) => a.annotations?.length > 0))
const needsPerCharSpans = computed(() => {
  const result = !!props.charStyle || !!props.clickableChar || !!props.draggableChar || hasCharAnnotations.value
  console.log('needsPerCharSpans =', result, {
    charStyle: !!props.charStyle,
    clickableChar: !!props.clickableChar,
    draggableChar: !!props.draggableChar,
    hasCharAnnotations: hasCharAnnotations.value,
  })
  return result
})

function wordDisplayText(sentenceIndex: number, segmentIndex: number, wordIndex: number): string {
  return clusteredChars.value[sentenceIndex][segmentIndex][wordIndex].map((c) => c.displayText).join('')
}

const myAnnotatedSentences = computed<Array<AnnotatedSentence>>(() => {
  const annotatedSentences = new Array<AnnotatedSentence>()
  let matchedCount = 0
  const unmatchedIds: string[] = []

  ;[...props.sentences]
    .toSorted((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10))
    .forEach((sentence: SentenceType, idx: number) => {
      const annotationSet = props.annotations.find((bma: AnnotationSetType) => bma.sentence.id === sentence.id)
      if (annotationSet) {
        matchedCount++
      } else {
        unmatchedIds.push(sentence.id)
      }
      let segmentation = [] as Array<number>
      if (props.segmentations.length && idx < props.segmentations.length) {
        segmentation = props.segmentations[idx]
      }
      const annotatedSentence = new AnnotatedSentence(sentence, annotationSet, segmentation, props.showEndOfSentenceCharacter)
      if (props.transformation?.transform) {
        props.transformation.transform(annotatedSentence)
      }
      annotatedSentences.push(annotatedSentence)
    })

  // Log per-set annotation counts for matched sets
  const matchDetails = [...props.sentences]
    .toSorted((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10))
    .map((s) => {
      const set = props.annotations.find((bma) => bma.sentence.id === s.id)
      return `${s.id}:${set ? set.annotations?.length ?? 0 : 'MISS'}`
    })
  console.log(
    `AnnotatedText myAnnotatedSentences: ${props.sentences.length} sentences, ${props.annotations.length} annotation sets, ${matchedCount} matched`,
    `\n  per-sentence annotation counts: [${matchDetails.join(', ')}]`,
  )

  return annotatedSentences
})

const myAnnotatedWords = computed<Array<Array<Array<AnnotatedWord>>>>(() => {
  const words = [] as Array<Array<Array<AnnotatedWord>>>
  if (!myAnnotatedSentences.value.length) {
    return words
  }

  myAnnotatedSentences.value.forEach((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => {
    words.push(new Array<Array<AnnotatedWord>>())
    annotatedSentence.forEach((annotatedSegment: AnnotatedSegment, __, segmentIndex = 0) => {
      words[sentenceIndex].push(new Array<AnnotatedWord>())
      annotatedSegment.forEach((annotatedWord: AnnotatedWord) => {
        words[sentenceIndex][segmentIndex].push(annotatedWord)
      })
    })
  })

  return words
})

const segmentBoundaries = computed<Array<Array<string>>>(() => {
  const segmentBoundaries = [] as Array<Array<string>>
  if (!myAnnotatedSentences.value.length) {
    return segmentBoundaries
  }

  myAnnotatedSentences.value.forEach((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => {
    segmentBoundaries.push(new Array<string>())
    annotatedSentence.forEach((__: AnnotatedSegment, segmentBoundary: string | null) => {
      segmentBoundaries[sentenceIndex].push(segmentBoundary ?? '')
    })
  })
  return segmentBoundaries
})

const segmentBoundaryStyles = computed<Array<Array<string>>>(() => {
  const segmentBoundaryStyles = [] as Array<Array<string>>
  if (!myAnnotatedSegments.value.length) {
    return segmentBoundaryStyles
  }

  myAnnotatedSegments.value.forEach((annotatedSegment: Array<AnnotatedSegment>, segmentIndex: number) => {
    segmentBoundaryStyles.push(new Array<string>())
    annotatedSegment.forEach(() => {
      segmentBoundaryStyles[segmentIndex].push(regularStyle)
    })
  })
  return segmentBoundaryStyles
})

const segmentStyles = computed<Array<Array<string>>>(() => {
  const segmentStyles = [] as Array<Array<string>>
  if (!myAnnotatedSegments.value.length) {
    return segmentStyles
  }

  myAnnotatedSegments.value.forEach((annotatedSegment: Array<AnnotatedSegment>, segmentIndex: number) => {
    segmentStyles.push(new Array<string>())
    annotatedSegment.forEach(() => {
      segmentStyles[segmentIndex].push(regularStyle)
    })
  })
  return segmentStyles
})

const sentenceStyles = computed<Array<string>>(() => {
  const sentenceStyles = [] as Array<string>
  if (!myAnnotatedSentences.value.length) {
    return sentenceStyles
  }

  myAnnotatedSentences.value.forEach(() => {
    sentenceStyles.push(regularStyle)
  })
  return sentenceStyles
})

const transformationClass = computed<string>(() => {
  return props.transformation?.props.class ?? ''
})

const wordBoundaries = computed<Array<Array<Array<string | null>>>>(() => {
  const wordBoundaries = [] as Array<Array<Array<string | null>>>
  if (!myAnnotatedSentences.value.length) {
    return wordBoundaries
  }

  myAnnotatedSentences.value.forEach((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => {
    wordBoundaries.push(new Array<Array<string>>())
    annotatedSentence.forEach((annotatedSegment: AnnotatedSegment, __, segmentIndex = 0) => {
      wordBoundaries[sentenceIndex].push(new Array<string>())
      annotatedSegment.forEach((__: AnnotatedWord, wordBoundary) => {
        wordBoundaries[sentenceIndex][segmentIndex].push(wordBoundary)
      })
    })
  })
  return wordBoundaries
})

const wordBoundaryStyles = computed<Array<Array<Array<string>>>>(() => {
  const wordBoundaryStyles = [] as Array<Array<Array<string>>>
  if (!myAnnotatedSentences.value.length) {
    return wordBoundaryStyles
  }
  myAnnotatedSentences.value.forEach((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => {
    wordBoundaryStyles.push(new Array<Array<string>>())
    annotatedSentence.forEach((annotatedSegment: AnnotatedSegment, __: string | null, segmentIndex = 0) => {
      wordBoundaryStyles[sentenceIndex].push(new Array<string>())
      annotatedSegment.forEach((__: AnnotatedWord, ___: string | null, ____ = 0) => {
        wordBoundaryStyles[sentenceIndex][segmentIndex].push(regularStyle)
      })
    })
  })
  return wordBoundaryStyles
})

const wordStyles = computed<Array<Array<Array<string>>>>(() => {
  const wordStyles = [] as Array<Array<Array<string>>>
  if (!myAnnotatedSentences.value.length) {
    return wordStyles
  }
  myAnnotatedSentences.value.forEach((annotatedSentence: AnnotatedSentence, sentenceIndex: number) => {
    wordStyles.push(new Array<Array<string>>())
    annotatedSentence.forEach((annotatedSegment: AnnotatedSegment, __: string | null, segmentIndex = 0) => {
      wordStyles[sentenceIndex].push(new Array<string>())
      annotatedSegment.forEach((__: AnnotatedWord, ___, ____) => {
        wordStyles[sentenceIndex][segmentIndex].push(regularStyle)
      })
    })
  })
  return wordStyles
})

// Pre-compute which words have a newlineAfter annotation on ANY character.
// Must check all characters (not just lastChar) because when sof pasuq is displayed,
// the annotation sits on the character before the sof pasuq, not on it.
const wordHasNewline = computed<boolean[][][]>(() => {
  const result = myAnnotatedWords.value.map((sentenceWords) =>
    sentenceWords.map((segmentWords) => segmentWords.map((word) => word.some((c) => !!c?.overrideStyle.newlineAfter))),
  )
  const totalNewlines = result.flat(2).filter(Boolean).length
  const totalWords = result.flat(2).length
  console.log(`AnnotatedText wordHasNewline: ${totalNewlines} newlines across ${totalWords} words, columnMode=${props.columnMode}`)
  return result
})

// Pre-compute which words have a stretchAfter annotation on ANY character.
const wordHasStretch = computed<boolean[][][]>(() => {
  const result = myAnnotatedWords.value.map((sentenceWords) =>
    sentenceWords.map((segmentWords) => segmentWords.map((word) => word.some((c) => !!c?.overrideStyle.stretchAfter))),
  )
  const totalStretches = result.flat(2).filter(Boolean).length
  console.log(`AnnotatedText wordHasStretch: ${totalStretches} stretch points`)
  return result
})

// Whether each sentence's last word ends with a forced newline (no inter-sentence space needed).
const sentenceEndsWithNewline = computed(() =>
  wordHasNewline.value.map((segments) => {
    const lastSeg = segments[segments.length - 1]
    return !!lastSeg?.[lastSeg.length - 1]
  }),
)

// Container ref for column-mode justification composable.
const columnContainerRef = ref<HTMLElement | null>(null)

useColumnJustification(
  columnContainerRef,
  computed(() => !!props.columnMode),
  [myAnnotatedWords, computed(() => props.fontSize), transformationClass],
)

function onLineNumberClick(annotatedSentence: AnnotatedSentence, sentenceIndex: number) {
  emit('clickLineNumber', annotatedSentence, sentenceIndex)
}

function onResponsiveHeaderClick(annotatedSentence: AnnotatedSentence, sentenceIndex: number) {
  emit('clickResponsiveHeader', annotatedSentence, sentenceIndex)
}

function onSentenceClick(annotatedSentence: AnnotatedSentence, sentenceIndex: number) {
  emit('clickSentence', annotatedSentence, sentenceIndex)
}

function onSegmentClick(
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  sentenceIndex: number,
  segmentIndex: number,
) {
  emit('clickSegment', annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)
}

function onSegmentBoundaryClick(
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  sentenceIndex: number,
  segmentIndex: number,
) {
  emit('clickSegmentBoundary', annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)
}

function onWordClick(
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  annotatedWord: AnnotatedWord,
  sentenceIndex: number,
  segmentIndex: number,
  wordIndex: number,
) {
  emit('clickWord', annotatedSentence, annotatedSegment, annotatedWord, sentenceIndex, segmentIndex, wordIndex)
}

function onWordBoundaryClick(
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  annotatedWord: AnnotatedWord,
  sentenceIndex: number,
  segmentIndex: number,
  wordIndex: number,
) {
  emit('clickWordBoundary', annotatedSentence, annotatedSegment, annotatedWord, sentenceIndex, segmentIndex, wordIndex)
}

// All char-level event handlers share the same 8-arg signature; each just forwards to the parent via emit.
type CharEventArgs = [AnnotatedSentence, AnnotatedSegment, AnnotatedWord, AnnotatedCharacter, number, number, number, number]

function onCharClick(...args: CharEventArgs) {
  emit('clickChar', ...args)
}

function onCharMousedown(...args: CharEventArgs) {
  emit('mousedownChar', ...args)
}

function onCharMouseenter(...args: CharEventArgs) {
  emit('mouseenterChar', ...args)
}

function onCharDblclick(...args: CharEventArgs) {
  emit('dblclickChar', ...args)
}

// Boundary drag support: forward mousedown/mouseenter from word/segment boundary spans
// to the char event handlers using the last character of the adjacent word. This lets users
// start or continue a drag on a space between words or segments.

/** The last word and character cluster of a word, i.e. what a boundary drag stands in for. */
function boundaryDragTarget(sentenceIndex: number, segmentIndex: number, wordIndex: number) {
  const wordClusters = clusteredChars.value[sentenceIndex]?.[segmentIndex]?.[wordIndex]
  const word = myAnnotatedWords.value[sentenceIndex]?.[segmentIndex]?.[wordIndex]
  if (!wordClusters?.length || !word) {
    return null
  }
  return { cluster: wordClusters[wordClusters.length - 1], word }
}

function onBoundaryMousedown(
  event: MouseEvent,
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  sentenceIndex: number,
  segmentIndex: number,
  wordIndex: number,
) {
  if (!props.draggableChar) {
    return
  }
  event.preventDefault()
  const target = boundaryDragTarget(sentenceIndex, segmentIndex, wordIndex)
  if (!target) {
    return
  }
  onCharMousedown(
    annotatedSentence,
    annotatedSegment,
    target.word,
    target.cluster.annotatedChar,
    sentenceIndex,
    segmentIndex,
    wordIndex,
    target.cluster.charIndex,
  )
}

function onBoundaryMouseenter(
  annotatedSentence: AnnotatedSentence,
  annotatedSegment: AnnotatedSegment,
  sentenceIndex: number,
  segmentIndex: number,
  wordIndex: number,
) {
  if (!props.draggableChar) {
    return
  }
  const target = boundaryDragTarget(sentenceIndex, segmentIndex, wordIndex)
  if (!target) {
    return
  }
  onCharMouseenter(
    annotatedSentence,
    annotatedSegment,
    target.word,
    target.cluster.annotatedChar,
    sentenceIndex,
    segmentIndex,
    wordIndex,
    target.cluster.charIndex,
  )
}

onMounted(() => {
  watch(
    myAnnotatedSentences,
    () => {
      emit('update:annotatedSentences', myAnnotatedSentences.value)
    },
    { immediate: true },
  )

  watch(
    myAnnotatedSegments,
    () => {
      emit('update:annotatedSegments', myAnnotatedSegments.value)
    },
    { immediate: true },
  )

  watch(
    myAnnotatedWords,
    () => {
      emit('update:annotatedWords', myAnnotatedWords.value)
    },
    { immediate: true },
  )
})
</script>

<template>
  <div :style="displayStyle">
    <div
      ref="columnContainerRef"
      :class="{ 'column-justify': props.columnMode }"
      dir="rtl"
      :style="[!props.columnMode && myAnnotatedSentences.length > 1 ? { 'text-align': 'justify', 'text-justify': 'inter-word' } : {}]"
      @mouseup="emit('mouseupText')"
    >
      <template v-for="(annotatedSentence, sentenceIndex) in myAnnotatedSentences" :key="`${props.name}:${sentenceIndex}-div:key`"
        ><span
          :class="transformationClass || props.defaultFont"
          :style="[{ fontWeight: 'bold', fontSize: props.fontSize + '%', lineHeight: props.lineHeight }]"
          ><span v-show="props.breakBeforeLine() && sentenceIndex > 0"><br /></span
          ><template v-if="props.showResponsiveHeaders()">
            <span
              :class="
                props.clickableResponsiveHeader && props.clickableResponsiveHeader(annotatedSentence, sentenceIndex)
                  ? 'clickableResponsiveHeader'
                  : ''
              "
              style="display: inline-block; width: 1.2em"
              @click="onResponsiveHeaderClick(annotatedSentence, sentenceIndex)"
            >
              <span v-if="annotatedSentence.isUpdating()">
                <v-progress-circular indeterminate />
              </span>
              <span v-else>
                <v-icon>
                  {{ annotatedSentence.sentence().isResponsive ? 'mdi-account-multiple' : 'mdi-account' }}
                </v-icon>
              </span>
            </span></template
          ><span
            v-show="props.showSentenceNumbers"
            :id="`${name}:${sentenceIndex}${NUMBER_ELEMENT_ID_SUFFIX}`"
            :class="props.clickableLineNumber && props.clickableLineNumber(annotatedSentence, sentenceIndex) ? 'clickableLineNumber' : ''"
            @click="onLineNumberClick(annotatedSentence, sentenceIndex)"
          >
            ({{ (props.startIndex !== null ? props.startIndex : 0) + sentenceIndex + 1 }})
          </span>
          <span
            :id="`${name}:${sentenceIndex}${SENTENCE_ELEMENT_ID_SUFFIX}`"
            :key="sentenceIndex + ':key'"
            :class="[
              sentenceStyles[sentenceIndex],
              props.sentenceStyle ? props.sentenceStyle(sentenceStyles[sentenceIndex], annotatedSentence, sentenceIndex) : '',
              props.clickableSentence && props.clickableSentence(annotatedSentence, sentenceIndex) ? 'clickableSentence' : '',
            ]"
            :style="props.sentenceInlineStyle?.(annotatedSentence, sentenceIndex)"
            @click="onSentenceClick(annotatedSentence, sentenceIndex)"
            ><template
              v-for="(annotatedSegment, segmentIndex) in myAnnotatedSegments[sentenceIndex]"
              :key="name + ':' + sentenceIndex + ':' + segmentIndex + ':key'"
            >
              <span
                :id="textSegmentId(name, sentenceIndex, segmentIndex)"
                :class="[
                  segmentStyles[sentenceIndex][segmentIndex],
                  props.segmentStyle
                    ? props.segmentStyle(
                        segmentStyles[sentenceIndex][segmentIndex],
                        annotatedSentence,
                        annotatedSegment,
                        sentenceIndex,
                        segmentIndex,
                      )
                    : '',
                  props.clickableSegment && props.clickableSegment(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)
                    ? 'clickableSegment'
                    : '',
                ]"
                @click="onSegmentClick(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)"
                ><template
                  v-for="(annotatedWord, wordIndex) in myAnnotatedWords[sentenceIndex][segmentIndex]"
                  :key="name + ':' + sentenceIndex + ':' + segmentIndex + ':' + wordIndex + ':key'"
                  ><span
                    :id="`${name}:${sentenceIndex}:${segmentIndex}:${wordIndex}${WORD_ELEMENT_ID_SUFFIX}`"
                    :class="[
                      wordStyles[sentenceIndex][segmentIndex][wordIndex],
                      props.wordStyle
                        ? props.wordStyle(
                            wordStyles[sentenceIndex][segmentIndex][wordIndex],
                            annotatedSentence,
                            annotatedSegment,
                            annotatedWord,
                            sentenceIndex,
                            segmentIndex,
                            wordIndex,
                          )
                        : '',
                      props.clickableWord &&
                      props.clickableWord(annotatedSentence, annotatedSegment, annotatedWord, sentenceIndex, segmentIndex, wordIndex)
                        ? 'clickableWord'
                        : '',
                      props.showNewlineIndicators && wordHasNewline[sentenceIndex]?.[segmentIndex]?.[wordIndex] ? 'show-newline-glyph' : '',
                    ]"
                    @click="onWordClick(annotatedSentence, annotatedSegment, annotatedWord, sentenceIndex, segmentIndex, wordIndex)"
                    ><template v-if="!needsPerCharSpans">{{ wordDisplayText(sentenceIndex, segmentIndex, wordIndex) }}</template
                    ><template v-else
                      ><template
                        v-for="cluster in clusteredChars[sentenceIndex][segmentIndex][wordIndex]"
                        :key="name + ':' + sentenceIndex + ':' + segmentIndex + ':' + wordIndex + ':' + cluster.charIndex + ':char-key'"
                        ><span
                          :id="name + ':' + sentenceIndex + ':' + segmentIndex + ':' + wordIndex + ':' + cluster.charIndex + ':char-id'"
                          :class="[
                            cluster.annotatedChar.styleClass,
                            props.charStyle
                              ? props.charStyle(
                                  cluster.annotatedChar.styleClass,
                                  annotatedSentence,
                                  annotatedSegment,
                                  annotatedWord,
                                  cluster.annotatedChar,
                                  sentenceIndex,
                                  segmentIndex,
                                  wordIndex,
                                  cluster.charIndex,
                                )
                              : '',
                            props.clickableChar &&
                            props.clickableChar(
                              annotatedSentence,
                              annotatedSegment,
                              annotatedWord,
                              cluster.annotatedChar,
                              sentenceIndex,
                              segmentIndex,
                              wordIndex,
                              cluster.charIndex,
                            )
                              ? 'clickableChar'
                              : '',
                            props.draggableChar &&
                            props.draggableChar(
                              annotatedSentence,
                              annotatedSegment,
                              annotatedWord,
                              cluster.annotatedChar,
                              sentenceIndex,
                              segmentIndex,
                              wordIndex,
                              cluster.charIndex,
                            )
                              ? 'draggableChar'
                              : '',
                            props.isCharSelected ? props.isCharSelected(cluster.annotatedChar) : '',
                          ]"
                          :style="[
                            props.suppressColors && props.suppressColors(sentenceIndex, segmentIndex)
                              ? {}
                              : characterStyleToCss(cluster.annotatedChar.overrideStyle),
                          ]"
                          @click="
                            onCharClick(
                              annotatedSentence,
                              annotatedSegment,
                              annotatedWord,
                              cluster.annotatedChar,
                              sentenceIndex,
                              segmentIndex,
                              wordIndex,
                              cluster.charIndex,
                            )
                          "
                          @dblclick.prevent="
                            onCharDblclick(
                              annotatedSentence,
                              annotatedSegment,
                              annotatedWord,
                              cluster.annotatedChar,
                              sentenceIndex,
                              segmentIndex,
                              wordIndex,
                              cluster.charIndex,
                            )
                          "
                          @mousedown.prevent="
                            onCharMousedown(
                              annotatedSentence,
                              annotatedSegment,
                              annotatedWord,
                              cluster.annotatedChar,
                              sentenceIndex,
                              segmentIndex,
                              wordIndex,
                              cluster.charIndex,
                            )
                          "
                          @mouseenter="
                            onCharMouseenter(
                              annotatedSentence,
                              annotatedSegment,
                              annotatedWord,
                              cluster.annotatedChar,
                              sentenceIndex,
                              segmentIndex,
                              wordIndex,
                              cluster.charIndex,
                            )
                          "
                          >{{ cluster.displayText }}</span
                        ></template
                      ></template
                    ></span
                  ><span
                    v-if="wordBoundaries[sentenceIndex][segmentIndex][wordIndex]"
                    :id="`${name}:${sentenceIndex}:${segmentIndex}:${wordIndex}${BOUNDARY_ELEMENT_ID_SUFFIX}`"
                    :key="name + ':' + sentenceIndex + ':' + segmentIndex + ':' + wordIndex + ':boundary-key'"
                    :class="[
                      wordBoundaryStyles[sentenceIndex][segmentIndex][wordIndex],
                      props.wordBoundaryStyle
                        ? props.wordBoundaryStyle(
                            wordStyles[sentenceIndex][segmentIndex][wordIndex],
                            annotatedSentence,
                            annotatedSegment,
                            annotatedWord,
                            sentenceIndex,
                            segmentIndex,
                            wordIndex,
                          )
                        : '',
                      props.clickableWordBoundary &&
                      props.clickableWordBoundary(
                        annotatedSentence,
                        annotatedSegment,
                        annotatedWord,
                        sentenceIndex,
                        segmentIndex,
                        wordIndex,
                      )
                        ? 'clickableWordBoundary'
                        : '',
                      props.isWordBoundarySelected ? props.isWordBoundarySelected(annotatedWord) : '',
                    ]"
                    @click="onWordBoundaryClick(annotatedSentence, annotatedSegment, annotatedWord, sentenceIndex, segmentIndex, wordIndex)"
                    @mousedown="onBoundaryMousedown($event, annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex, wordIndex)"
                    @mouseenter="onBoundaryMouseenter(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex, wordIndex)"
                    >{{ wordBoundaries[sentenceIndex][segmentIndex][wordIndex] }}</span
                  ><span v-if="wordHasStretch[sentenceIndex]?.[segmentIndex]?.[wordIndex]" :class="STRETCH_POINT_CLASS">&nbsp;</span
                  ><template v-if="wordHasNewline[sentenceIndex]?.[segmentIndex]?.[wordIndex]"
                    ><span v-if="props.columnMode" :class="COLUMN_LINE_FILLER_CLASS">&nbsp;</span><br
                  /></template> </template></span
              ><span
                v-if="segmentBoundaries[sentenceIndex][segmentIndex]"
                :id="`${name}:${sentenceIndex}:${segmentIndex}${BOUNDARY_ELEMENT_ID_SUFFIX}`"
                :key="name + ':' + sentenceIndex + ':' + segmentIndex + ':boundary-key'"
                :class="[
                  segmentBoundaryStyles[sentenceIndex][segmentIndex],
                  props.segmentBoundaryStyle
                    ? props.segmentBoundaryStyle(
                        segmentStyles[sentenceIndex][segmentIndex],
                        annotatedSentence,
                        annotatedSegment,
                        sentenceIndex,
                        segmentIndex,
                      )
                    : {},
                  props.clickableSegmentBoundary &&
                  props.clickableSegmentBoundary(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)
                    ? 'clickableSegmentBoundary'
                    : '',
                  props.isSegmentBoundarySelected ? props.isSegmentBoundarySelected(annotatedSegment) : '',
                ]"
                @click="onSegmentBoundaryClick(annotatedSentence, annotatedSegment, sentenceIndex, segmentIndex)"
                @mousedown="
                  onBoundaryMousedown(
                    $event,
                    annotatedSentence,
                    annotatedSegment,
                    sentenceIndex,
                    segmentIndex,
                    myAnnotatedWords[sentenceIndex][segmentIndex].length - 1,
                  )
                "
                @mouseenter="
                  onBoundaryMouseenter(
                    annotatedSentence,
                    annotatedSegment,
                    sentenceIndex,
                    segmentIndex,
                    myAnnotatedWords[sentenceIndex][segmentIndex].length - 1,
                  )
                "
                >{{ segmentBoundaries[sentenceIndex][segmentIndex] }}</span
              ></template
            ><span v-if="!props.columnMode || !sentenceEndsWithNewline[sentenceIndex]" :class="{ 'column-sentence-gap': props.columnMode }"
              >&nbsp;</span
            ></span
          ></span
        ><slot :annotated-sentence="annotatedSentence" name="sentence" :sentence-index="sentenceIndex" />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* theme independent */
.annotated-label {
  position: relative;
  top: 10px;
  left: 10px;
  font-size: 12px;
  background-color: rgb(var(--v-theme-surface));
  padding: 0 4px;
}

.regularStyle {
  font-weight: normal;
  border-style: none;
  font-size: 100%;
}

.absentStyle {
  color: rgba(var(--v-theme-on-surface), 0.35);
}

.waitingStyle {
  background-color: rgba(63, 81, 181, 0.3);
}

.userSpeakingStyle {
  background-color: rgba(255, 235, 59, 0.5);
}

.playBackStyle {
  background-color: rgba(149, 117, 205, 0.15);
}

.segmentBoundaryStyle {
  border: 2px solid rgb(var(--v-theme-primary));
  border-radius: 4px;
  padding: 2px 4px;
}

.wordBoundaryStyle {
  border: 1px dashed rgb(var(--v-theme-secondary));
  border-radius: 3px;
  padding: 1px 3px;
}

.badWordsStyle {
  color: rgb(var(--v-theme-error));
}

.segmentStyle0 {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
}

.segmentStyle1 {
  background-color: rgba(var(--v-theme-on-surface), 0.18);
}

.annotated-label {
  background-color: rgb(var(--v-theme-surface));
}

.clickableLineNumber:hover {
  cursor: pointer;
  color: rgb(var(--v-theme-primary));
}

.clickableSentence {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.clickableSentence:hover {
  cursor: pointer;
  color: rgb(var(--v-theme-primary));
  filter: brightness(0.95);
}

.clickableSegment:hover {
  cursor: pointer;
  color: rgb(var(--v-theme-primary));
}

.clickableSegmentBoundary:hover {
  cursor: pointer;
}

.clickableResponsiveHeader:hover {
  cursor: pointer;
}

.clickableWord:hover {
  cursor: pointer;
  color: rgb(var(--v-theme-primary));
}

.clickableWordBoundary:hover {
  cursor: pointer;
}

.clickableChar:hover {
  cursor: pointer;
  color: rgb(var(--v-theme-primary));
}

.draggableChar {
  cursor: crosshair;
  user-select: none;
}

.colorationSelected {
  border-top: 2px solid rgb(var(--v-theme-info));
  border-bottom: 2px solid rgb(var(--v-theme-info));
  padding: 2px 0;
}

/* RTL: "start" = rightmost (first char), "end" = leftmost (last char) */
.colorationSelectedStart {
  border-inline-start: 2px solid rgb(var(--v-theme-info));
  border-start-start-radius: 3px;
  border-end-start-radius: 3px;
  padding-inline-start: 2px;
}

.colorationSelectedEnd {
  border-inline-end: 2px solid rgb(var(--v-theme-info));
  border-start-end-radius: 3px;
  border-end-end-radius: 3px;
  padding-inline-end: 2px;
}

.show-newline-glyph::after {
  content: '\21B5';
  font-size: 0.5em;
  color: rgba(var(--v-theme-on-surface), 0.3);
  vertical-align: super;
  user-select: none;
  font-family: sans-serif;
  font-weight: normal;
  background: transparent;
}

/* Stretch points are invisible inline elements whose width is set dynamically
   by the column justification composable to fill section-marker gaps. */
.stretch-point {
  display: inline-block;
  width: 0;
  overflow: hidden;
  color: transparent;
  vertical-align: baseline;
}

/* In column mode, the space between sentences must not carry the sentence's
   background color — otherwise it bleeds onto the next line after a <br>. */
.column-sentence-gap {
  background: transparent;
}

/* Invisible inline-block filler injected at the end of each column line.
   The composable sets its width dynamically for short lines (petucha breaks)
   to absorb slack so justify doesn't over-spread those few words. */
.column-line-filler {
  display: inline-block;
  width: 0;
  overflow: hidden;
  vertical-align: baseline;
}

/* Column mode justification — text-align-last is needed because every line
   ends with a forced <br> break, and CSS justify skips lines before forced breaks
   unless text-align-last is explicitly set to justify. */
.column-justify {
  text-align: justify;
  text-align-last: justify;
  text-justify: inter-word;
}

/* Temporarily disable justify during measurement so natural line widths
   can be measured without justify distortion. */
.column-measuring {
  text-align: start !important;
  text-align-last: auto !important;
}
</style>
